import { isEnabled } from 'Config';
import { debugConfigService as debug } from 'Lib/debug';
import { localStorage as storage } from 'Lib/storage';

import { SESSION_LEVERS_TTL_MS } from 'Store/session/constants';

import {
  CONFIG_SERVICE_READ,
  CONFIG_SERVICE_RECEIVE,
  KEY_CONFIG_SERVICE_PERSIST,
  KEY_CONFIG_SERVICE_PERSIST_OVERRIDE,
} from './constants';
import { REGISTERED_LEVER_VALUES } from './levers';
import type {
  LeverConfig,
  LeverConfigDehydrated,
  LeverConfigLogPayload,
  LeverConfigStore,
  LeverConfigType,
  LeverConfigValue,
  LeverKey,
} from './types';

// It is possible to encounter unregistered levers that we want to keep, such as when the
// backend returns lever values before the client has been updated to consume them.
// However, we've seen so far that it's more common for them to be deprecated levers that have not
// been removed from the meta levers payload.
// We may want to start to persist them again in the future and use the staleness heuristic, but
// for now, we'll just drop them from the persisted data.
const PERSIST_UNREGISTERED_LEVERS = false;

export function inferLeverType(value: LeverConfigValue): LeverConfigType {
  switch (typeof value) {
    case 'boolean':
      return 'boolean';
    case 'number':
      return 'number';
    case 'string':
    default:
      return 'string';
  }
}

export function dehydrateLever<T extends LeverKey>(
  lever: LeverConfig<T>
): LeverConfigDehydrated<T> | undefined | null {
  const { value, pid, type, time } = lever;
  if (value == null && !pid) {
    return null;
  }
  return [value, pid, type, time];
}

export function rehydrateLever<T extends LeverKey>(
  lever: LeverConfigDehydrated<T>
): LeverConfig<T> | null {
  const [value, pid = '', type = inferLeverType(value), time = Date.now()] = lever;
  if (value == null && !pid) {
    return null;
  }
  return { value, pid, type, time };
}

export function loadFromStorage(): Partial<LeverConfigStore> {
  try {
    const { levers: persistLevers = {} } =
      JSON.parse(storage.getItem(KEY_CONFIG_SERVICE_PERSIST) || '{}') || {};
    const persistLeverOverrides = isEnabled('devTools')
      ? JSON.parse(storage.getItem(KEY_CONFIG_SERVICE_PERSIST_OVERRIDE) || '{}') || {}
      : {};

    const levers: Partial<LeverConfigStore> = {};
    const persistLeversKeys = Object.keys(persistLevers) as LeverKey[];
    persistLeversKeys.forEach(<T extends LeverKey>(key: T) => {
      const lever: LeverConfigDehydrated<T> = persistLevers[key];
      try {
        const stored = Array.isArray(lever) && rehydrateLever(lever);
        const override = persistLeverOverrides[key];
        if (stored || override != null) {
          levers[key] = {};
          if (stored) {
            levers[key]!.stored = stored;
          }
          if (override != null) {
            levers[key]!.override = { value: override };
          }
        }
      } catch (e) {
        debug(`bad data for lever ${key}: ${JSON.stringify(lever)}`);
      }
    });
    debug('levers loaded');
    return levers;
  } catch (e) {
    debug('bad stored data');
    return {};
  }
}

export function saveToStorage(levers: LeverConfigStore): void {
  const persistLevers = {};
  const persistLeverOverrides = {};
  const time = Date.now();
  let hasOverrides = false;

  // Clean up the data
  Object.keys(levers).forEach((key) => {
    const stored = levers[key]?.stored || {};
    const override = levers[key]?.override?.value;
    if (
      // persist if lever is registered
      key in REGISTERED_LEVER_VALUES ||
      // persist if lever is not registered but value is fresh
      (PERSIST_UNREGISTERED_LEVERS && time - stored.time < SESSION_LEVERS_TTL_MS)
    ) {
      const lever = dehydrateLever(stored);
      if (lever) {
        persistLevers[key] = lever;
      }
    }
    // Overrides are optional
    if (override != null) {
      persistLeverOverrides[key] = override;
      hasOverrides = true;
    }
  });

  const configServiceData = { levers: persistLevers };
  storage.setItem(KEY_CONFIG_SERVICE_PERSIST, JSON.stringify(configServiceData));

  debug(`${Object.keys(persistLevers).length} / ${Object.keys(levers).length} levers persisted`);

  if (isEnabled('devTools') && hasOverrides) {
    storage.setItem(KEY_CONFIG_SERVICE_PERSIST_OVERRIDE, JSON.stringify(persistLeverOverrides));
    debug('lever overrides persisted');
  } else {
    storage.removeItem(KEY_CONFIG_SERVICE_PERSIST_OVERRIDE);
  }
}

export function createLogger() {
  return function log<T extends LeverKey>(
    type: string,
    { key: name, pid, value }: LeverConfigLogPayload<T>
  ) {
    // Note: Backend logging was high volume and unused, so it has been disabled per TDP-82428
    switch (type) {
      case CONFIG_SERVICE_RECEIVE:
        debug(`[receive] ${name}: ${value.toString()}${pid ? ` (pid: ${pid})` : ''}`);
        break;
      case CONFIG_SERVICE_READ:
        debug(`[read] ${name}: ${value.toString()}${pid ? ` (pid: ${pid})` : ''}`);
        break;
      default:
        break;
    }
  };
}

export const lockIdGen = (
  (i = 0, max = 36 ** 2) =>
  () => {
    const id = i.toString(36).padStart(8, '0');
    i = (i + 1) % max; // eslint-disable-line no-param-reassign
    return id;
  }
)();
